import { WallDatumAbstractionProvider } from "building-generation/elements/datums/datumAbstractionProviders";
import { foundationSections, foundationSlabs } from "building-generation/elements/foundation/foundationProfiles";
import {
  collectWallFinishFaces,
  collectWallFinishFaces2,
} from "building-generation/elements/wallAssemblies/wallFinishFaces";
import { iterateBuildingGeometries } from "building-geometry/geometry/buildingGeometry";
import { collectBuildingProps } from "building-geometry/props/collectBuildingProps";
import type { Building } from "building-types/building/buildingTypes";
import { FloorFinishType } from "building-types/floors/floorFinishTypes";
import { lineRenderGeometryOptionsNew } from "building-types/geometry/buildingGeometryTypes";
import { FaceCategory } from "building-types/materials/faceCategories";
import { RoomClass } from "building-types/rooms/roomTypesEnum";
import { GLTF_ASSET_SCALE } from "common/constants/assetConstants";
import { assertIsNotUndefined } from "common/utils/assertOfType";
import { degreeToRadian } from "common/utils/math";
import { filter3DProp } from "doc/plans/sheetGeometryEffects";
import type { SheetGeometrySelectorEffectMap } from "doc/plans/SheetGeometrySelectorEffectMap";
import { mergedPropLibraryNew } from "doc/propTools";
import { defaultExteriorPalette } from "doc/settings/defaultExteriorMaterials";
import { defaultInteriorPalette } from "doc/settings/interiorMaterialSettingsLibrary";
import { faceNew, facesByAABB3, type BRep, type Extrusion, type Face } from "geom/brep";
import { extrusionToBRep } from "geom/extrude";
import { mat4Ident, mat4Mult, mat4Rotate, mat4Scale, mat4Translate } from "geom/mat4";
import { planeNewXY } from "geom/plane";
import { polyLine2Copy, polyLine2Reverse } from "geom/polyLine2";
import { transformFace } from "geom/transform";

const exteriorPalette = defaultExteriorPalette();
const interiorPalette = defaultInteriorPalette();
const outlinePropFaceCategories = new Set([
  FaceCategory.PROP_APPLIANCES,
  FaceCategory.PROP_FIXTURES,
  FaceCategory.PROP_FURNITURE,
  FaceCategory.PROP_OTHER,
]);

interface BuildingOutlineVertexCategory {
  faces: Face[];
  debugColor: number;
}

function faceOuterLoopToArray(face: Face, facesOut: Face[]) {
  if (face.loops.length === 1) {
    facesOut.push(face);
    return;
  }
  facesOut.push(faceNew([face.loops[0]], face.plane));
}

function extrusionToArray(extrusion: Extrusion, facesOut: Face[]) {
  const extrusionMesh = extrusionToBRep(extrusion);
  facesOut.push(...extrusionMesh.faces);
}

function brepToVertexArray(brep: BRep, facesOut: Face[]) {
  facesOut.push(...brep.faces);
}

/**
 * Analogous to renderWalls, except renders only exterior wall outer loops.
 */
function renderOutlineWalls(building: Building, facesOut: Face[] = []): Face[] {
  const { levels, levelsAttributes, walls, wallSweeps, settings, mirror, datumAbstractions, enabledFeatures } =
    building;
  const { wall: wallSettings, wallCatalog: wallCatalogSettings } = settings;

  const wallDatumAbstractionProvider = new WallDatumAbstractionProvider(levels, levelsAttributes, datumAbstractions);

  for (const levelId in levels) {
    const levelWallFinishFaces =
      enabledFeatures.wallPlanes || enabledFeatures.collectWallFinishFaces2
        ? collectWallFinishFaces2({
            wallDatumAbstractionProvider,
            wallSystem: walls[levelId],
            wallSweeps: wallSweeps[levelId],
            wallCatalogSettings,
            mirror,
            outlineFaces: true,
          })
        : collectWallFinishFaces({
            wallDatumAbstractionProvider,
            wallSystem: walls[levelId],
            wallSweeps: wallSweeps[levelId],
            wallSettings,
            wallCatalogSettings,
            interiorPalette,
            exteriorPalette,
            mirror,
            outlineFaces: true,
          });

    const {
      exteriorWallFaces,
      exteriorWallCapFaces,
      exteriorWallExtrusions,
      exteriorWallBreps,
      exteriorWallTrimFaces,
      coreFinishFaces,
    } = levelWallFinishFaces;

    for (const blockId in exteriorWallFaces) {
      const exteriorRoomWalls = exteriorWallFaces[blockId];
      for (const m of exteriorRoomWalls) {
        for (const face of m.faces) {
          faceOuterLoopToArray(face, facesOut);
        }
      }
    }

    for (const blockId in exteriorWallCapFaces) {
      const exteriorRoomWalls = exteriorWallCapFaces[blockId];
      for (const m of exteriorRoomWalls) {
        for (const face of m.faces) {
          faceOuterLoopToArray(face, facesOut);
        }
      }
    }

    for (const blockId in exteriorWallExtrusions) {
      const extrusions = exteriorWallExtrusions[blockId];
      for (const m of extrusions) {
        extrusionToArray(m.extrusion, facesOut);
      }
    }

    for (const blockId in exteriorWallBreps) {
      for (const m of exteriorWallBreps[blockId]) {
        brepToVertexArray(m.brep, facesOut);
      }
    }

    for (const blockId in exteriorWallTrimFaces) {
      for (const m of exteriorWallTrimFaces[blockId]) {
        for (const face of m.faces) {
          faceOuterLoopToArray(face, facesOut);
        }
      }
    }

    if (enabledFeatures.wallPlanes) {
      for (const face of coreFinishFaces) {
        faceOuterLoopToArray(face, facesOut);
      }
    }
  }
  return facesOut;
}

/**
 * Analogous to renderRoofs, except renders only rooftops.
 */
function renderOutlineRoofs(building: Building, facesOut: Face[] = []): Face[] {
  iterateBuildingGeometries(
    building,
    new Set<FaceCategory>([FaceCategory.ROOF_TOP]),
    lineRenderGeometryOptionsNew(false, undefined),
    ({ faceCategory, faces }) => {
      if (faceCategory === FaceCategory.ROOF_TOP) {
        for (const face of faces) {
          faceOuterLoopToArray(face, facesOut);
        }
      }
    },
  );

  return facesOut;
}

/**
 * Analogous to renderFlooring.
 */
function renderOutlineFlooring(building: Building, facesOut: Face[] = []): Face[] {
  const { floorSystems, rooms } = building;
  for (const roomId in rooms) {
    const room = rooms[roomId];

    const {
      level: { id: levelId },
    } = room;

    if (room.attributes.roomClass === RoomClass.EXTERIOR) {
      continue;
    }

    const floorFinishes = floorSystems[levelId].roomFloorFinishes[roomId];
    for (const finish of floorFinishes) {
      switch (finish.finishType) {
        case FloorFinishType.EXTERIOR:
        case FloorFinishType.INTERIOR: {
          const { finishFace } = finish;
          faceOuterLoopToArray(finishFace, facesOut);
          continue;
        }
      }
    }
  }
  return facesOut;
}

function renderOutlineFoundation(building: Building, facesOut: Face[] = []): Face[] {
  const { foundations, foundationSuperSystems } = building;

  for (const foundationId in foundations) {
    const foundation = foundations[foundationId];

    if (!foundation) {
      continue;
    }

    for (const section of foundationSections(foundation)) {
      for (const extrusion of section.extrusions) {
        extrusionToArray(extrusion, facesOut);
      }
    }
  }

  for (const foundationSuperSystemId in foundationSuperSystems) {
    const foundationSuperSystem = foundationSuperSystems[foundationSuperSystemId];

    if (!foundationSuperSystem) {
      continue;
    }

    for (const slab of foundationSlabs(foundationSuperSystem)) {
      extrusionToArray(slab.extrusion, facesOut);
    }
  }
  return facesOut;
}

function renderOutlineStairs(building: Building, facesOut: Face[] = []): Face[] {
  const { stairSystems } = building;
  for (const key in stairSystems) {
    const { blocks } = stairSystems[key];

    for (const blockId in blocks) {
      const block = blocks[blockId];
      const { stairPlanPolys } = block;
      for (const poly of stairPlanPolys) {
        assertIsNotUndefined(poly);
        const outlineFlipped = polyLine2Reverse(polyLine2Copy(poly.outline));
        const stairFace = faceNew([outlineFlipped], planeNewXY(0, 0, poly.height));
        facesOut.push(stairFace);
      }
    }
  }
  return facesOut;
}

function renderOutlineRailings(building: Building, facesOut: Face[] = []): Face[] {
  const { railings } = building;
  for (const key in railings) {
    const railing = railings[key];
    facesOut.push(...railing.railingPathFacesForBuildingOutlineAndLighting);
    for (const extrusion of railing.buildingOutlineExtrusions) {
      extrusionToArray(extrusion, facesOut);
    }
  }
  return facesOut;
}

function renderOutlineOpenings(building: Building, facesOut: Face[] = []): Face[] {
  const { doorUnits, windowUnits } = building;
  // Add awnings
  for (const windowId in windowUnits) {
    const window = windowUnits[windowId];
    for (const face of window.awningRoof.geom) {
      if (face.type === "FACE") {
        facesOut.push(face);
      }
    }
  }
  for (const doorId in doorUnits) {
    const door = doorUnits[doorId];
    for (const face of door.awningRoof.geom) {
      if (face.type === "FACE") {
        facesOut.push(face);
      }
    }
  }
  return facesOut;
}

async function renderOutlineProps({
  building,
  selectors,
  facesOut = [],
}: {
  building: Building;
  selectors?: SheetGeometrySelectorEffectMap[];
  facesOut?: Face[];
}): Promise<Face[]> {
  const scaleMat = mat4Ident();
  const translateMat = mat4Ident();
  const rotateMat = mat4Ident();
  const rotateScaleMat = mat4Ident();
  const propTransform = mat4Ident();
  const props = collectBuildingProps(building, outlinePropFaceCategories);
  const mergedPropLibrary = mergedPropLibraryNew(building.doc.assets);
  for (const prop of props) {
    if (selectors && !filter3DProp({ prop, selectors })) {
      continue;
    }
    const propAsset = await mergedPropLibrary.get(prop.assetId);
    if (!propAsset) {
      continue;
    }
    const bb = propAsset.boundingBox;
    mat4Scale(prop.scaleX * GLTF_ASSET_SCALE, prop.scaleY * GLTF_ASSET_SCALE, prop.scaleZ * GLTF_ASSET_SCALE, scaleMat);
    mat4Rotate(degreeToRadian(prop.rotationDegrees), 0, 0, 1, rotateMat);
    mat4Translate(prop.x, prop.y, prop.z, translateMat);

    mat4Mult(rotateMat, scaleMat, rotateScaleMat);
    mat4Mult(translateMat, rotateScaleMat, propTransform);

    const faces: Face[] = facesByAABB3(bb);
    for (const face of faces) {
      transformFace(face, propTransform);
    }
    facesOut.push(...faces);
  }
  return facesOut;
}

export async function renderBuildingOutlineVertexCategories(
  building: Building,
): Promise<BuildingOutlineVertexCategory[]> {
  const categories: BuildingOutlineVertexCategory[] = [];

  categories.push({ faces: renderOutlineWalls(building), debugColor: 0xff0000 });
  categories.push({ faces: renderOutlineRoofs(building), debugColor: 0x00ff00 });
  categories.push({ faces: renderOutlineFlooring(building), debugColor: 0x0000ff });
  categories.push({ faces: renderOutlineFoundation(building), debugColor: 0xffff00 });
  categories.push({ faces: renderOutlineOpenings(building), debugColor: 0xff8800 });
  categories.push({ faces: renderOutlineStairs(building), debugColor: 0x00ffff });
  categories.push({ faces: renderOutlineRailings(building), debugColor: 0x00ffff });
  categories.push({ faces: await renderOutlineProps({ building }), debugColor: 0xff00ff });

  return categories;
}

export async function renderBuildingOutlineFaces(
  building: Building,
  propSelectors?: SheetGeometrySelectorEffectMap[],
): Promise<Face[]> {
  const faces: Face[] = [];

  renderOutlineWalls(building, faces);
  renderOutlineRoofs(building, faces);
  renderOutlineFlooring(building, faces);
  renderOutlineFoundation(building, faces);
  renderOutlineOpenings(building, faces);
  renderOutlineStairs(building, faces);
  renderOutlineRailings(building, faces);
  await renderOutlineProps({
    building,
    selectors: propSelectors,
    facesOut: faces,
  });

  return faces;
}
